// 114 — Bitácora UCI Neonatal (Fase 4, Tanda 6a, ver PLAN.md "FASE 4 — spec
// completa" → "114 — Bitácora UCI Neonatal"). Migrado del motor genérico
// compartido `cuidadoIntensivo.tsx` a un componente propio, mismo criterio
// que 113 (Tanda 5a/5b) — leído directamente de las páginas 227-228 del PDF
// fuente, footer real confirmado "SNS-MSP/HCU-form.114/2021" (misma familia
// MSP que 051/113).
//
// Alcance — Bloques A (header MSP) + B (constantes vitales de ingreso) +
// C (datos de bitácora, diagnóstico de ingreso, gráfico de temperatura,
// gráfico de PA/pulso/FR) + D (medicación) + E (ingesta: ingresos/egresos +
// balance) + G (gasometría y otros exámenes) — Tanda 6a. Más F
// (oxigenoterapia y ventilación mecánica), H (procedimientos), I
// (procedimientos de enfermería), J (solicitud de exámenes), K
// (interconsultas) y L (informes de enfermería) — Tanda 8b, leídos de la
// página 228 del PDF fuente, reusando `TablaConColumnas`/`ChecklistTurno`
// construidos en la Tanda 8a de 113. Alcance completo. Bloque M
// (datos del profesional responsable por turno AM/PM/HS): la HOJA de 114 (pág. 228) no imprime el cuadro,
// pero el INSTRUCTIVO oficial (pág. 232) lo lista (fecha de la consulta, hora 24 h, nombres y apellidos,
// número de documento = cédula, firma, sello) — T6, 2026-09-23: se implementa con el instructivo como
// fuente (ver `profesionalPorTurnoDatos.ts`: el turno que firma lo arma el servidor).
//
// FHIR: Encounter class=ACUTE + extensión de nivel de cuidado intensivo
// (mecanismo compartido de `cuidadoIntensivo.tsx`), 1 Condition si el
// diagnóstico de ingreso trae texto (el papel imprime un solo campo ancho,
// no 5 filas como 113), y FC/FR/Temperatura/SatO2 + presión arterial
// (panel con 2 component, mismo patrón que Formulario020) crean Observation
// reusando los LOINC ya definidos en `vitalSigns.ts`. Tensión arterial
// MEDIA queda como `Par[]` de respaldo — no hay un LOINC ya establecido
// para TAM en el catálogo compartido, y no se justifica inventar uno para
// un solo consumidor. El resto (datos de bitácora, medicación, ingesta,
// gasometría) es `Par[]` de respaldo, mismo criterio que 113.
//
// Turno horario: el papel de 114 arranca la grilla en las 7 (no en las 8
// como 113/el motor UCI compartido) — se reusa el mismo turno 8→7 de
// `TablaHoraria24h` de todas formas (equivalencia funcional, no facsímil:
// qué hora exacta ancla el turno de 24h es un detalle operativo del
// establecimiento, no clínico, y no se justifica parametrizar el motor
// compartido por una diferencia de 1 hora).
import type { DatosGraficoConstantesUciPdf } from '@logiacare/hce-pdf';
import { balanceHidricoEfectivo, type ConfigBalanceHidrico } from './calculosClinicosDatos';
import type { FilaTablaHoraria24h } from './uci/TablaHoraria24h';
import { resumenFilaTablaHoraria24h, COLUMNAS_HORARIAS_UCI, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialGraficoEnfermeria, type PuntosGraficoEnfermeria, type SerieGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { estadoInicialChecklistTurno, resumenItemChecklistTurno, type EstadoChecklistTurno, type ItemChecklistTurno } from './uci/ChecklistTurno';
import { estadoInicialFilasNombrables, paresDeFilasNombrables, tablaConGruposNombrables, type EstadoFilasNombrables } from './uci/filasNombrables';
import { estadoInicialAlergiasSangre, paresAlergiasSangre, type EstadoAlergiasSangre } from './uci/datosClinicosUci';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { CAMPOS_PROFESIONAL_114, estadoInicialProfesionalPorTurno, paresProfesionalPorTurno, type EstadoProfesionalPorTurno } from './profesionalPorTurnoDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

// ---------- B. Constantes vitales de ingreso ----------

export const CAMPOS_CONSTANTES_INGRESO_114 = ['fr', 'fc', 'taSistolica', 'taDiastolica', 'taMedia', 'temperatura', 'saturacionOxigeno'] as const;
export type CampoConstantesIngreso114 = (typeof CAMPOS_CONSTANTES_INGRESO_114)[number];

export type EstadoConstantesIngreso114 = Readonly<Record<CampoConstantesIngreso114, string>>;

export function estadoInicialConstantesIngreso114(): EstadoConstantesIngreso114 {
  return Object.fromEntries(CAMPOS_CONSTANTES_INGRESO_114.map((c) => [c, ''])) as EstadoConstantesIngreso114;
}

function paresDeConstantesIngreso114(t: Traducir, e: EstadoConstantesIngreso114): Par[] {
  return CAMPOS_CONSTANTES_INGRESO_114.map((c) => ({ pregunta: t(`formularios.f114.constantesIngreso.${c}`), respuesta: e[c].trim() }));
}

// ---------- A/C. Condición edad + unidad neonatal (selects de una sola opción) ----------

export const OPCIONES_CONDICION_EDAD_114 = ['horas', 'dias', 'meses', 'anios'] as const;
export type CondicionEdad114 = (typeof OPCIONES_CONDICION_EDAD_114)[number];

export const OPCIONES_UNIDAD_NEONATAL_114 = ['incubadora', 'cunaCalorRadiante', 'termocuna', 'cunaCorriente'] as const;
export type UnidadNeonatal114 = (typeof OPCIONES_UNIDAD_NEONATAL_114)[number];

// ---------- C. Bitácora — datos generales + diagnóstico ----------

/** Ola 2 (M2): alergias Sí/No + especificar, grupo sanguíneo y factor Rh separados (instructivo de la bitácora UCI, bloque C). */
export interface EstadoDatosBitacora114 extends EstadoAlergiasSangre {
  readonly fechaIngresoUci: string;
  readonly fechaActual: string;
  readonly pesoDiario: string;
  readonly condicionEdad: CondicionEdad114 | '';
  readonly unidadNeonatal: UnidadNeonatal114 | '';
}

export function estadoInicialDatosBitacora114(): EstadoDatosBitacora114 {
  return { fechaIngresoUci: '', fechaActual: '', pesoDiario: '', condicionEdad: '', unidadNeonatal: '', ...estadoInicialAlergiasSangre() };
}

function paresDeDatosBitacora114(t: Traducir, e: EstadoDatosBitacora114): Par[] {
  return [
    { pregunta: t('formularios.f114.datosBitacora.fechaIngresoUci'), respuesta: e.fechaIngresoUci.trim() },
    { pregunta: t('formularios.f114.datosBitacora.fechaActual'), respuesta: e.fechaActual.trim() },
    ...paresAlergiasSangre(t, t('formularios.f114.datosBitacora.alergias'), e),
    { pregunta: t('formularios.f114.datosBitacora.pesoDiario'), respuesta: e.pesoDiario.trim() },
    { pregunta: t('formularios.f114.datosBitacora.condicionEdad'), respuesta: e.condicionEdad ? t(`formularios.f114.condicionEdadOpciones.${e.condicionEdad}`) : '' },
    { pregunta: t('formularios.f114.datosBitacora.unidadNeonatal'), respuesta: e.unidadNeonatal ? t(`formularios.f114.unidadNeonatalOpciones.${e.unidadNeonatal}`) : '' },
  ];
}

// ---------- C. Bitácora — gráfico de temperatura ----------

/** Serie única, escala 34-40°C, símbolo X — confirmado leyendo la página 227 del PDF fuente. */
export const SERIE_GRAFICO_TEMPERATURA_114: readonly SerieGraficoEnfermeria[] = [
  { clave: 'temperatura', etiqueta: 'Temperatura', simbolo: 'cruz', color: '#b5563c', escala: { min: 34, max: 40 } },
];

// ---------- C. Bitácora — gráfico de PA/pulso/FR ----------

/**
 * 4 series sobre la misma escala 0-200 — confirmado leyendo la página 227:
 * presión arterial sistólica/diastólica (el papel las marca con ">"/"<"
 * bracketando el rango — se aproxima con triángulo/círculo hueco, mismos 5
 * símbolos ya soportados por `GraficoEnfermeria`, sin inventar un glifo
 * nuevo: equivalencia funcional, no facsímil), pulso (•, círculo relleno) y
 * frecuencia respiratoria (X, cruz).
 */
export const SERIES_GRAFICO_PA_PULSO_FR_114: readonly SerieGraficoEnfermeria[] = [
  { clave: 'presionArterialSistolica', etiqueta: 'Presión arterial sistólica', simbolo: 'triangulo', color: '#1f4d47', escala: { min: 0, max: 200 } },
  { clave: 'presionArterialDiastolica', etiqueta: 'Presión arterial diastólica', simbolo: 'circuloHueco', color: '#b5563c', escala: { min: 0, max: 200 } },
  { clave: 'pulso', etiqueta: 'Pulso', simbolo: 'circulo', color: '#6f8f6a', escala: { min: 0, max: 200 } },
  { clave: 'frecuenciaRespiratoria', etiqueta: 'Frecuencia respiratoria', simbolo: 'cruz', color: '#153733', escala: { min: 0, max: 200 } },
];

function diagramaDeGrafico(series: readonly SerieGraficoEnfermeria[], puntos: PuntosGraficoEnfermeria): DatosGraficoConstantesUciPdf {
  return {
    series: series.map((serie) => ({
      clave: serie.clave,
      etiqueta: serie.etiqueta,
      simbolo: serie.simbolo,
      escalaMin: serie.escala.min,
      escalaMax: serie.escala.max,
      variantes: serie.variantes?.map((v) => ({ clave: v.clave, simbolo: v.simbolo })),
      puntos: COLUMNAS_HORARIAS_UCI.filter((c) => puntos[serie.clave]?.[c] !== undefined).map((c) => ({
        columna: c,
        valor: puntos[serie.clave]![c]!.valor,
        variante: puntos[serie.clave]![c]!.variante,
      })),
    })),
  };
}

/**
 * Serializa los 2 gráficos de 114 (temperatura, PA/pulso/FR) a 2
 * `DatosGraficoConstantesUciPdf` INDEPENDIENTES — el papel los imprime como
 * 2 cajas separadas, cada una con su propia grilla, no una sola caja con 5
 * series superpuestas (a diferencia de las 4 series de 113, que sí
 * comparten una sola caja). Pura, exportada para test.
 */
export function diagramasGraficoConstantesUciDeEstado114(temperatura: PuntosGraficoEnfermeria, paPulsoFr: PuntosGraficoEnfermeria): readonly DatosGraficoConstantesUciPdf[] {
  return [diagramaDeGrafico(SERIE_GRAFICO_TEMPERATURA_114, temperatura), diagramaDeGrafico(SERIES_GRAFICO_PA_PULSO_FR_114, paPulsoFr)];
}

// ---------- D. Medicación ----------

// Ola 2 (M2): el papel deja ~14 renglones en blanco para medicación (D), uno o dos más para nebulizaciones y ~16 para "ingresos
// intravenosos" (E) — conteo aproximado sobre el escaneo — más 2-3 renglones libres al final de gasometría/otros exámenes y de la
// ventilación (F). Cada renglón lleva NOMBRE + 24 horas (`uci/filasNombrables.ts`, tope 20; 4 en los "otros"). Los "ingresos
// intravenosos" suman al TOTAL 1 del balance con la clave sintética `ingresosIntravenosos` (la de la fila única anterior).

export const TOPE_OTROS_HORARIOS_114 = 4;

export interface GruposNombrables114 {
  readonly medicacion: EstadoFilasNombrables;
  readonly nebulizaciones: EstadoFilasNombrables;
  readonly ingresosIntravenosos: EstadoFilasNombrables;
  readonly otrosGasometria: EstadoFilasNombrables;
  readonly otrosVentilacion: EstadoFilasNombrables;
}

export function estadoInicialGruposNombrables114(): GruposNombrables114 {
  return {
    medicacion: estadoInicialFilasNombrables(4),
    nebulizaciones: estadoInicialFilasNombrables(2),
    ingresosIntravenosos: estadoInicialFilasNombrables(4),
    otrosGasometria: estadoInicialFilasNombrables(1),
    otrosVentilacion: estadoInicialFilasNombrables(1),
  };
}

// ---------- E. Ingesta — ingresos ----------

export const FILAS_INGESTA_INGRESOS_114: readonly FilaTablaHoraria24h[] = [
  { clave: 'lecheMaterna', etiqueta: 'Leche materna', unidad: 'mL' },
  { clave: 'formula', etiqueta: 'Fórmula', unidad: 'mL' },
  { clave: 'porSuccion', etiqueta: 'Por succión', unidad: 'mL' },
  { clave: 'porSonda', etiqueta: 'Por sonda', unidad: 'mL' },
  { clave: 'gastroclisisInfusionContinua', etiqueta: 'Gastroclisis o infusión contínua', unidad: 'mL' },
];

// ---------- E. Ingesta — egresos ----------

export const FILAS_INGESTA_EGRESOS_114: readonly FilaTablaHoraria24h[] = [
  { clave: 'insensibles', etiqueta: 'Insensibles', unidad: 'mL' },
  { clave: 'drenajes', etiqueta: 'Drenajes', unidad: 'mL' },
  { clave: 'otros', etiqueta: 'Otros', unidad: 'mL' },
  { clave: 'extracciones', etiqueta: 'Extracciones', unidad: 'mL' },
  { clave: 'diuresisHoraria', etiqueta: 'Diuresis horaria', unidad: 'mL' },
];

// ---------- E. Ingesta — balance hídrico (calculado; editable solo si se sobrescribe) ----------
// El instructivo (pág. 231 del PDF) dice: "Realizar la suma de ingresos para obtener un total, igualmente en los egresos. Registrar el
// balance diario y balance global". Por eso se calculan desde las filas horarias; cada campo sigue siendo editable (lo escrito a propósito
// sobrescribe el cálculo, vaciarlo vuelve a él). El BALANCE GLOBAL (acumulado de todos los días) no sale de una sola hoja: queda manual.

export const CAMPOS_BALANCE_HIDRICO_114 = ['total1', 'total2', 'sumaIngresos', 'total3', 'total4', 'sumaEgresos', 'balanceDiario', 'balanceGlobal'] as const;
export type CampoBalanceHidrico114 = (typeof CAMPOS_BALANCE_HIDRICO_114)[number];

/** Lo que la enfermera SOBRESCRIBIÓ a mano (`''` = usar el cálculo). Misma forma que antes: lo ya guardado sigue siendo un valor manual válido. */
export type EstadoBalanceHidrico114 = Readonly<Record<CampoBalanceHidrico114, string>>;

/**
 * TOTAL 1 = ingresos intravenosos; TOTAL 2 = vía oral (leche materna, fórmula, succión, sonda, gastroclisis/infusión continua);
 * TOTAL 3 = pérdidas (insensibles, drenajes, otros, extracciones); TOTAL 4 = orina (diuresis horaria) — los cuatro bloques del papel
 * (pág. 227). SUMA ingresos = TOTAL 1+2; SUMA egresos = TOTAL 3+4; BALANCE DIARIO = suma de ingresos − suma de egresos (solo con ambas sumas).
 */
export const CONFIG_BALANCE_HIDRICO_114: ConfigBalanceHidrico<CampoBalanceHidrico114> = {
  totalesIngresos: {
    total1: ['ingresosIntravenosos'],
    total2: ['lecheMaterna', 'formula', 'porSuccion', 'porSonda', 'gastroclisisInfusionContinua'],
  },
  totalesEgresos: {
    total3: ['insensibles', 'drenajes', 'otros', 'extracciones'],
    total4: ['diuresisHoraria'],
  },
  sumaIngresos: 'sumaIngresos',
  sumaEgresos: 'sumaEgresos',
  balanceDiario: 'balanceDiario',
};

/** Campos del balance que salen del cálculo (todos menos el BALANCE GLOBAL acumulado, que solo se puede escribir a mano). */
export const CAMPOS_BALANCE_CALCULABLES_114: readonly CampoBalanceHidrico114[] = CAMPOS_BALANCE_HIDRICO_114.filter((c) => c !== 'balanceGlobal');

/** Valor que se ve e imprime de cada campo: lo sobrescrito a mano o, si no, el calculado desde ingresos/egresos. Pura, exportada para test. */
export function balanceHidricoEfectivo114(manual: EstadoBalanceHidrico114, ingresos: EstadoTablaHoraria24h, egresos: EstadoTablaHoraria24h): EstadoBalanceHidrico114 {
  return balanceHidricoEfectivo(CONFIG_BALANCE_HIDRICO_114, CAMPOS_BALANCE_HIDRICO_114, manual, ingresos, egresos);
}

export function estadoInicialBalanceHidrico114(): EstadoBalanceHidrico114 {
  return Object.fromEntries(CAMPOS_BALANCE_HIDRICO_114.map((c) => [c, ''])) as EstadoBalanceHidrico114;
}

function paresDeBalanceHidrico114(t: Traducir, e: EstadoBalanceHidrico114): Par[] {
  return CAMPOS_BALANCE_HIDRICO_114.map((c) => ({ pregunta: t(`formularios.f114.balanceHidrico.${c}`), respuesta: e[c].trim() }));
}

// ---------- G. Gasometría y otros exámenes ----------

export const FILAS_GASOMETRIA_114: readonly FilaTablaHoraria24h[] = [
  { clave: 'ph', etiqueta: 'pH' },
  { clave: 'pco2', etiqueta: 'pCO2' },
  { clave: 'po2', etiqueta: 'pO2' },
  { clave: 'hco3', etiqueta: 'HCO3' },
  { clave: 'sao2', etiqueta: 'SaO2' },
  { clave: 'excesoBase', etiqueta: 'Exceso de base' },
  { clave: 'lactato', etiqueta: 'Lactato' },
  { clave: 'sodio', etiqueta: 'Sodio' },
  { clave: 'potasio', etiqueta: 'Potasio' },
  { clave: 'calcio', etiqueta: 'Calcio' },
  { clave: 'hematocrito', etiqueta: 'Hematocrito' },
  { clave: 'glucosa', etiqueta: 'Glucosa' },
  { clave: 'bilirrubinas', etiqueta: 'Bilirrubinas' },
  { clave: 'densidadUrinaria', etiqueta: 'Densidad urinaria' },
];

// ---------- F. Oxigenoterapia y ventilación mecánica (Tanda 8b) ----------
// Leída de la página 228 del PDF fuente — 16 filas nombradas (17+ en
// blanco en el papel, no se transcriben, mismo criterio que 113 D).

export const FILAS_VENTILACION_114: readonly FilaTablaHoraria24h[] = [
  { clave: 'oxigenoPor', etiqueta: 'Oxígeno por' },
  { clave: 'tipoVentilacion', etiqueta: 'Tipo ventilación' },
  { clave: 'modo', etiqueta: 'Modo' },
  { clave: 'fio2', etiqueta: 'FiO2' },
  { clave: 'flujo', etiqueta: 'Flujo' },
  { clave: 'volumenMinuto', etiqueta: 'Volumen minuto' },
  { clave: 'volumenTidal', etiqueta: 'Volumen tidal' },
  { clave: 'pip', etiqueta: 'PIP' },
  { clave: 'peep', etiqueta: 'PEEP' },
  { clave: 'tiempoInspiracion', etiqueta: 'Tiempo inspiración' },
  { clave: 'tiempoEspiracion', etiqueta: 'Tiempo espiración' },
  { clave: 'fr', etiqueta: 'FR' },
  { clave: 'ie', etiqueta: 'I/E' },
  { clave: 'pmva', etiqueta: 'PMVA' },
  { clave: 'amplitud', etiqueta: 'Amplitud' },
  { clave: 'hertzios', etiqueta: 'Hertzios' },
];

// ---------- H. Procedimientos (Tanda 8b) ----------
// Tabla fija de 8 columnas (más ancha que la de 113: número de
// catéter/dispositivo, sitio y días de colocación no tienen equivalente en
// 113) — ver `TablaConColumnas`.

export const FILAS_PROCEDIMIENTOS_114: readonly FilaTablaConColumnas[] = [
  { clave: 'cateterVenosoCentral', etiqueta: 'Catéter venoso central' },
  { clave: 'cateterPercutaneoPicc', etiqueta: 'Catéter percutáneo (PICC)' },
  { clave: 'cateterVenosoPeriferico', etiqueta: 'Catéter venoso periférico' },
  { clave: 'sondaNasogastrica', etiqueta: 'Sonda nasogástrica' },
  { clave: 'sondaVesical', etiqueta: 'Sonda vesical' },
  { clave: 'cateterUmbilical', etiqueta: 'Catéter umbilical' },
  { clave: 'viaEnteral', etiqueta: 'Vía enteral' },
  { clave: 'exsanguineoTransfusion', etiqueta: 'Exsanguineo transfusión' },
];

/** Renglón en blanco al final de la tabla H del papel (1, contado en la pág. 228): el nombre del procedimiento se escribe en la primera columna. I. Procedimientos de enfermería (19 ítems) no trae renglones en blanco. */
export const FILAS_PROCEDIMIENTOS_OTROS_114: readonly FilaTablaConColumnas[] = [
  { clave: 'otro1', etiqueta: '1' },
];

export const COLUMNAS_PROCEDIMIENTOS_114: readonly ColumnaTablaConColumnas[] = [
  { clave: 'numeroCateter', etiqueta: 'Número de catéter o dispositivo' },
  { clave: 'fechaColocacion', etiqueta: 'Fecha de colocación', tipo: 'fecha' },
  { clave: 'sitioColocacion', etiqueta: 'Sitio de colocación' },
  { clave: 'diasColocacion', etiqueta: 'Días de colocación' },
  { clave: 'fechaCambio', etiqueta: 'Fecha de cambio', tipo: 'fecha' },
  { clave: 'fechaRetiro', etiqueta: 'Fecha de retiro', tipo: 'fecha' },
  { clave: 'fechaCuracion', etiqueta: 'Fecha de curación', tipo: 'fecha' },
  { clave: 'nombreResponsable', etiqueta: 'Nombre del responsable' },
];

export const COLUMNAS_PROCEDIMIENTOS_OTROS_114: readonly ColumnaTablaConColumnas[] = [{ clave: 'nombre', etiqueta: 'Otro procedimiento' }, ...COLUMNAS_PROCEDIMIENTOS_114];

// ---------- I. Procedimientos de enfermería (Tanda 8b) ----------
// Checklist AM/PM/HS (19 ítems) — ver `ChecklistTurno`. El papel agrega, en las MISMAS 19 filas (la grilla de la derecha está alineada con
// cada ítem, pág. 228), seis columnas: HORA MAÑANA / REALIZADO POR / HORA TARDE / REALIZADO POR / HORA NOCHE / REALIZADO POR. Ola 2 (M2): se
// capturan por ítem (`EstadoProcedimientosEnfermeriaHoras114`) y en el `Par[]` se unen al par del ítem. Antes se transcribían como un
// único registro por turno (`EstadoResponsableProcedimientosEnfermeria114`, ya retirado): lo firmado antes conserva sus pares viejos.

export const ITEMS_PROCEDIMIENTOS_ENFERMERIA_114: readonly ItemChecklistTurno[] = [
  { clave: 'bano', etiqueta: 'Baño' },
  { clave: 'valoracionFisicaRecienNacido', etiqueta: 'Valoración física del recién nacido' },
  { clave: 'aseoGenitalesConCadaCambioPanal', etiqueta: 'Aseo de genitales con cada cambio de pañal' },
  { clave: 'aseoCavidadOral', etiqueta: 'Aseo de cavidad oral' },
  { clave: 'cuidadoCordonUmbilical', etiqueta: 'Cuidado cordón umbilical' },
  { clave: 'aspiracionSecreciones', etiqueta: 'Aspiración de secreciones' },
  { clave: 'aseoOcular', etiqueta: 'Aseo ocular' },
  { clave: 'cambioAntifaz', etiqueta: 'Cambio de antifaz' },
  { clave: 'apoyoProcedimientosEspeciales', etiqueta: 'Apoyo procedimientos especiales' },
  { clave: 'higieneParcial', etiqueta: 'Higiene parcial' },
  { clave: 'educacionAyudaManejo', etiqueta: 'Educación y ayuda del manejo' },
  { clave: 'aislamiento', etiqueta: 'Aislamiento' },
  { clave: 'dilatacionPupilas', etiqueta: 'Dilatación de pupilas' },
  { clave: 'fisioterapiaRespiratoria', etiqueta: 'Fisioterapia respiratoria' },
  { clave: 'cambiosPosicion', etiqueta: 'Cambios de posición' },
  { clave: 'rotacionSensores', etiqueta: 'Rotación de sensores' },
  { clave: 'aplicacionMediosFisicos', etiqueta: 'Aplicación de medios físicos' },
  { clave: 'limpiezaDiariaUnidad', etiqueta: 'Limpieza diaria de la unidad' },
  { clave: 'medidasContencion', etiqueta: 'Medidas de contención' },
];

/** Las 19 filas de la grilla de horas/responsables (mismas claves que el checklist de al lado). */
export const FILAS_PROCEDIMIENTOS_ENFERMERIA_114: readonly FilaTablaConColumnas[] = ITEMS_PROCEDIMIENTOS_ENFERMERIA_114.map((i) => ({ clave: i.clave, etiqueta: i.etiqueta }));

export const COLUMNAS_PROCEDIMIENTOS_ENFERMERIA_HORAS_114: readonly ColumnaTablaConColumnas[] = [
  { clave: 'horaManana', etiqueta: 'Hora mañana' },
  { clave: 'realizadoPorManana', etiqueta: 'Realizado por (mañana)' },
  { clave: 'horaTarde', etiqueta: 'Hora tarde' },
  { clave: 'realizadoPorTarde', etiqueta: 'Realizado por (tarde)' },
  { clave: 'horaNoche', etiqueta: 'Hora noche' },
  { clave: 'realizadoPorNoche', etiqueta: 'Realizado por (noche)' },
];

/**
 * Un par por ítem con dato: los turnos marcados y, a continuación, las horas y responsables de esa fila ("Baño" = "AM,PM · Hora mañana:08:00
 * · Realizado por (mañana):Enf. Pérez"). Un ítem sin marcas pero con hora/responsable sale igual: el dato no se pierde.
 */
export function paresProcedimientosEnfermeria114(checklist: EstadoChecklistTurno, horas: EstadoTablaConColumnas): Par[] {
  return ITEMS_PROCEDIMIENTOS_ENFERMERIA_114.flatMap((i) => {
    const respuesta = [resumenItemChecklistTurno(checklist, i.clave), resumenFilaTablaConColumnas(horas, i.clave, COLUMNAS_PROCEDIMIENTOS_ENFERMERIA_HORAS_114)].filter((p) => p !== '').join(' · ');
    return respuesta === '' ? [] : [{ pregunta: i.etiqueta, respuesta }];
  });
}

// ---------- J. Solicitud de exámenes (Tanda 8b) ----------
// 4 filas en blanco en el papel (menos que las 5 de 113 — cada formulario
// UCI deja su propio número fijo de filas, transcrito verbatim).

export const NUM_FILAS_SOLICITUD_EXAMENES_114 = 5; // el papel trae ~5 renglones (antes 4)

export const FILAS_SOLICITUD_EXAMENES_114: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_SOLICITUD_EXAMENES_114 }, (_, i) => ({
  clave: `examen${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_SOLICITUD_EXAMENES_114: readonly ColumnaTablaConColumnas[] = [
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'fechaSolicitud', etiqueta: 'Fecha de solicitud', tipo: 'fecha' },
  { clave: 'fechaRealizacion', etiqueta: 'Fecha de realización', tipo: 'fecha' },
];

// ---------- K. Interconsultas (Tanda 8b) ----------

export const NUM_FILAS_INTERCONSULTAS_114 = 5; // el papel trae ~5 renglones (antes 4)

export const FILAS_INTERCONSULTAS_114: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_INTERCONSULTAS_114 }, (_, i) => ({
  clave: `interconsulta${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_INTERCONSULTAS_114: readonly ColumnaTablaConColumnas[] = [
  { clave: 'servicio', etiqueta: 'Servicio interconsultado' },
  { clave: 'fechaSolicitud', etiqueta: 'Fecha de solicitud', tipo: 'fecha' },
  { clave: 'fechaRespuesta', etiqueta: 'Fecha de respuesta', tipo: 'fecha' },
];

// ---------- L. Informes de enfermería (Tanda 8b) ----------
// Mañana/Tarde/Noche (no AM/PM/HS como 113/J) — texto libre, mismo
// mecanismo que `EstadoInformeEnfermeria113`.

export interface EstadoInformeEnfermeria114 {
  readonly manana: string;
  readonly tarde: string;
  readonly noche: string;
}

export function estadoInicialInformeEnfermeria114(): EstadoInformeEnfermeria114 {
  return { manana: '', tarde: '', noche: '' };
}

function paresDeInformeEnfermeria114(t: Traducir, e: EstadoInformeEnfermeria114): Par[] {
  return [
    { pregunta: t('formularios.f114.informeEnfermeria.manana'), respuesta: e.manana.trim() },
    { pregunta: t('formularios.f114.informeEnfermeria.tarde'), respuesta: e.tarde.trim() },
    { pregunta: t('formularios.f114.informeEnfermeria.noche'), respuesta: e.noche.trim() },
  ];
}

// ---------- Estado combinado ----------

export interface EstadoBitacora114 {
  readonly constantesIngreso: EstadoConstantesIngreso114;
  readonly datosBitacora: EstadoDatosBitacora114;
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly graficoTemperatura: PuntosGraficoEnfermeria;
  readonly graficoPaPulsoFr: PuntosGraficoEnfermeria;
  readonly grupos: GruposNombrables114;
  readonly ingestaIngresos: EstadoTablaHoraria24h;
  readonly ingestaEgresos: EstadoTablaHoraria24h;
  readonly balanceHidrico: EstadoBalanceHidrico114;
  readonly gasometria: EstadoTablaHoraria24h;
  readonly ventilacion: EstadoTablaHoraria24h;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly procedimientosEnfermeria: EstadoChecklistTurno;
  readonly procedimientosEnfermeriaHoras: EstadoTablaConColumnas;
  readonly procedimientosOtros: EstadoTablaConColumnas;
  readonly solicitudExamenes: EstadoTablaConColumnas;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly informeEnfermeria: EstadoInformeEnfermeria114;
  readonly profesionalTurnos: EstadoProfesionalPorTurno;
}

export function estadoInicialBitacora114(): EstadoBitacora114 {
  return {
    constantesIngreso: estadoInicialConstantesIngreso114(),
    datosBitacora: estadoInicialDatosBitacora114(),
    diagnostico: { texto: '', cie10: null },
    graficoTemperatura: estadoInicialGraficoEnfermeria(SERIE_GRAFICO_TEMPERATURA_114),
    graficoPaPulsoFr: estadoInicialGraficoEnfermeria(SERIES_GRAFICO_PA_PULSO_FR_114),
    grupos: estadoInicialGruposNombrables114(),
    ingestaIngresos: estadoInicialTablaHoraria24h(FILAS_INGESTA_INGRESOS_114),
    ingestaEgresos: estadoInicialTablaHoraria24h(FILAS_INGESTA_EGRESOS_114),
    balanceHidrico: estadoInicialBalanceHidrico114(),
    gasometria: estadoInicialTablaHoraria24h(FILAS_GASOMETRIA_114),
    ventilacion: estadoInicialTablaHoraria24h(FILAS_VENTILACION_114),
    procedimientos: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_114, COLUMNAS_PROCEDIMIENTOS_114),
    procedimientosEnfermeria: estadoInicialChecklistTurno(ITEMS_PROCEDIMIENTOS_ENFERMERIA_114),
    procedimientosEnfermeriaHoras: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_ENFERMERIA_114, COLUMNAS_PROCEDIMIENTOS_ENFERMERIA_HORAS_114),
    procedimientosOtros: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_OTROS_114, COLUMNAS_PROCEDIMIENTOS_OTROS_114),
    solicitudExamenes: estadoInicialTablaConColumnas(FILAS_SOLICITUD_EXAMENES_114, COLUMNAS_SOLICITUD_EXAMENES_114),
    interconsultas: estadoInicialTablaConColumnas(FILAS_INTERCONSULTAS_114, COLUMNAS_INTERCONSULTAS_114),
    informeEnfermeria: estadoInicialInformeEnfermeria114(),
    profesionalTurnos: estadoInicialProfesionalPorTurno(),
  };
}

function paresDeFilasHorarias(filas: readonly FilaTablaHoraria24h[], estado: EstadoTablaHoraria24h): Par[] {
  return filas
    .map((f) => ({ pregunta: f.etiqueta, respuesta: resumenFilaTablaHoraria24h(estado, f.clave) }))
    .filter((p) => p.respuesta !== '');
}

function paresDeFilasConColumnas(filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas, prefijoEtiqueta: string): Par[] {
  return filas
    .map((f) => ({ pregunta: `${prefijoEtiqueta} ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }))
    .filter((p) => p.respuesta !== '');
}

/** Tablas de ingresos y egresos con los renglones nombrables ya sumados (fila sintética `ingresosIntravenosos`) — lo que lee el balance. */
export function tablasBalance114(estado: Pick<EstadoBitacora114, 'ingestaIngresos' | 'ingestaEgresos' | 'grupos'>): { ingresos: EstadoTablaHoraria24h; egresos: EstadoTablaHoraria24h } {
  return {
    ingresos: tablaConGruposNombrables(estado.ingestaIngresos, { ingresosIntravenosos: estado.grupos.ingresosIntravenosos }),
    egresos: estado.ingestaEgresos,
  };
}

/** "Otros" procedimientos: "<Procedimientos> — <nombre>" con sus datos; un renglón sin nombre sale numerado. */
function paresDeProcedimientosOtros114(t: Traducir, estado: EstadoTablaConColumnas): Par[] {
  const titulo = t('formularios.f114.procedimientosTitulo');
  return FILAS_PROCEDIMIENTOS_OTROS_114.flatMap((f) => {
    const nombre = (estado[f.clave]?.nombre ?? '').trim();
    const datos = resumenFilaTablaConColumnas(estado, f.clave, COLUMNAS_PROCEDIMIENTOS_114);
    if (nombre === '' && datos === '') return [];
    return [{ pregunta: `${titulo} — ${nombre !== '' ? nombre : `${t('formularios.uci.textosFijos.otroRenglon')} ${f.etiqueta}`}`, respuesta: datos || t('formularios.uci.textosFijos.sinDatos') }];
  });
}

/** Serializa toda la bitácora de 114 a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora114(t: Traducir, estado: EstadoBitacora114): Par[] {
  const { ingresos, egresos } = tablasBalance114(estado);
  const g = estado.grupos;
  const pares: Par[] = [
    ...paresDeConstantesIngreso114(t, estado.constantesIngreso),
    ...paresDeDatosBitacora114(t, estado.datosBitacora),
    { pregunta: t('formularios.f114.diagnosticoIngreso'), respuesta: conCodigoCie(estado.diagnostico) },
    ...paresDeFilasNombrables(t('formularios.f114.medicacionTitulo'), g.medicacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasNombrables(t('formularios.f114.nebulizacionesTitulo'), g.nebulizaciones, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasNombrables(t('formularios.f114.ingresosIntravenososTitulo'), g.ingresosIntravenosos, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_INGESTA_INGRESOS_114, estado.ingestaIngresos),
    ...paresDeFilasHorarias(FILAS_INGESTA_EGRESOS_114, estado.ingestaEgresos),
    ...paresDeBalanceHidrico114(t, balanceHidricoEfectivo114(estado.balanceHidrico, ingresos, egresos)),
    ...paresDeFilasHorarias(FILAS_GASOMETRIA_114, estado.gasometria),
    ...paresDeFilasNombrables(t('formularios.f114.otrosGasometriaTitulo'), g.otrosGasometria, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_VENTILACION_114, estado.ventilacion),
    ...paresDeFilasNombrables(t('formularios.f114.otrosVentilacionTitulo'), g.otrosVentilacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasConColumnas(FILAS_PROCEDIMIENTOS_114, COLUMNAS_PROCEDIMIENTOS_114, estado.procedimientos, t('formularios.f114.procedimientosTitulo')),
    ...paresDeProcedimientosOtros114(t, estado.procedimientosOtros),
    ...paresProcedimientosEnfermeria114(estado.procedimientosEnfermeria, estado.procedimientosEnfermeriaHoras),
    ...paresDeFilasConColumnas(FILAS_SOLICITUD_EXAMENES_114, COLUMNAS_SOLICITUD_EXAMENES_114, estado.solicitudExamenes, t('formularios.f114.solicitudExamenesTitulo')),
    ...paresDeFilasConColumnas(FILAS_INTERCONSULTAS_114, COLUMNAS_INTERCONSULTAS_114, estado.interconsultas, t('formularios.f114.interconsultasTitulo')),
    ...paresDeInformeEnfermeria114(t, estado.informeEnfermeria),
    ...paresProfesionalPorTurno(t, 'formularios.f114.profesionalTurnos', CAMPOS_PROFESIONAL_114, estado.profesionalTurnos),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
